import {
  CloseButton,
  Combobox,
  ComboboxItem,
  ComboboxOptionProps,
  ComboboxProps,
  InputBase,
  TextInputProps,
  useCombobox,
} from "@mantine/core";
import { useIntersection } from "@mantine/hooks";
import { FC, ReactNode, useMemo, useRef } from "react";
import styles from "./AsyncSearchableSelect.module.scss";

interface IData {
  value: string;
  label: string;
  store: "apple" | "google";
}

interface InfiniteScrollSelectProps {
  data: IData[];
  onChange: (value: number) => void;
  mah?: number | string;
  textInputProps?: TextInputProps;
  containerProps?: ComboboxProps;
  optionProps?: ComboboxOptionProps;
  error?: string | boolean;
  value: ComboboxItem | null;
  children?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  width?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  clearable?: boolean;
  onClear?: () => void;
  renderOption?: (item: IData) => ReactNode;
  isSelectable?: (item: IData) => boolean;
  onInvalidSelect?: (item: IData) => void;
}

const AsyncSearchableSelect: FC<InfiniteScrollSelectProps> = ({
  data = [],
  onChange,
  mah = 200,
  textInputProps = {},
  containerProps = {},
  optionProps = {},
  error,
  width,
  searchValue,
  onSearchChange,
  placeholder,
  clearable = false,
  onClear,
  value,
  renderOption,
  isSelectable,
  onInvalidSelect,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref } = useIntersection({
    root: containerRef.current,
    threshold: 1,
  });

  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
    onDropdownClose: () => {
      combobox.focusTarget();
    },
  });

  const optionsList: ReactNode = useMemo(() => {
    return (
      <>
        {data.map((item, index) => (
          <Combobox.Option
            ref={index === data.length - 1 ? ref : null}
            key={`${item.value}-${index}`}
            onClick={() => {
              if (!isSelectable || isSelectable(item)) {
                onChange(Number(item.value));
              } else {
                onInvalidSelect?.(item);
              }

              combobox.closeDropdown();
            }}
            {...optionProps}
            value={item.value}
            classNames={{ option: styles.option }}
          >
            {renderOption?.(item) ?? item.label}
          </Combobox.Option>
        ))}
      </>
    );
  }, [data]);

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={() => {
        combobox.closeDropdown();
        onSearchChange("");
      }}
      {...containerProps}
    >
      <Combobox.Target>
        <InputBase
          disabled={disabled}
          readOnly
          placeholder={placeholder}
          value={value?.label ?? ""}
          error={error}
          classNames={{
            input: styles.input,
          }}
          style={{
            width: width === "sm" ? 418 : width === "md" ? 397 : "100%",
          }}
          onClick={() => {
            if (!disabled) {
              combobox.toggleDropdown();
            }
          }}
          rightSection={
            disabled ? null : clearable && value ? (
              <CloseButton
                size="sm"
                onMouseDown={(event) => event.preventDefault()}
                onClick={onClear}
                aria-label="Clear value"
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          {...textInputProps}
        />
      </Combobox.Target>

      <Combobox.Dropdown
        mah={mah}
        ref={containerRef}
        style={{ overflowY: "auto", borderRadius: "12px" }}
        classNames={{
          dropdown: styles.dropdown,
        }}
      >
        <Combobox.Search
          value={searchValue}
          classNames={{
            input: styles.dropdown,
          }}
          onChange={(event) => {
            onSearchChange(event.currentTarget.value);
          }}
        />
        {data.length > 0 ? (
          optionsList
        ) : (
          <div style={{ padding: "10px", textAlign: "center" }}>No data</div>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default AsyncSearchableSelect;
